import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import {
  api,
  collectComments,
  type Comment,
  type Sentiment,
  type GeneralTag,
  type SafetyTag,
  SAFETY_TAGS,
} from "../lib/api";
import {
  FacetBars,
  GENERAL_TAG_LABELS,
  SENTIMENT_LABELS,
  SAFETY_TAG_LABELS,
} from "../components/FacetBars";
import { CommentRow } from "../components/VideoCommentIntelligence";
import {
  HowItWorksCallout,
  PrototypeNote,
} from "../components/youtube/explainers";

/**
 * Phase 6d.3 → Phase 8 #9.4 — channel-wide Comments dashboard, v8 multi-facet.
 *
 * The v7 single-drill (category → theme → comments) is replaced by four
 * INDEPENDENT, AND-composable facets:
 *   sentiment (exactly 1 per comment) · general_tags · topic_tags · safety_tags
 *
 * Three regions:
 *   1. Filter chip bar — active filters from URL params (click × to remove);
 *      also hosts the safety toggles + high-engagement toggle.
 *   2. Facet charts — sentiment (colored) + general tags + topics, each a
 *      click-to-filter FacetBars. Counts come from the overview blob
 *      (channel-wide) or comment-stats (when scoped to a video). Safety has no
 *      server-side rollup, so it lives as toggle chips, not a chart.
 *   3. Paginated comments list — same CommentRow as the per-video drill-down.
 *
 * URL contract: ?sentiment=&gtag=&topic=&safety=&videoId=&he=1 — every facet is
 * independent, so copying the URL reproduces the exact composed view.
 */

const PAGE_SIZE = 50;
const TOP_TOPICS_LIMIT = 20;

export function CommentsDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sentiment = (searchParams.get("sentiment") || "") as Sentiment | "";
  const gtag = (searchParams.get("gtag") || "") as GeneralTag | "";
  const topic = searchParams.get("topic") || "";
  const safety = (searchParams.get("safety") || "") as SafetyTag | "";
  const videoId = searchParams.get("videoId") || "";
  // Computed filter (not a tag): likeCount/replyCount over threshold.
  const highEngagement = searchParams.get("he") === "1";

  // Toggle a single URL param: setting the same value again clears it (so a
  // second click on an active facet bar deselects it).
  const toggleParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (next.get(key) === value || !value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: false });
  };

  const clearParam = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: false });
  };

  const overview = useQuery({
    queryKey: ["overview"],
    queryFn: api.overview,
  });

  const videoStats = useQuery({
    queryKey: ["commentStats", videoId],
    queryFn: () => api.commentStats(videoId),
    enabled: !!videoId,
  });

  const video = useQuery({
    queryKey: ["video", videoId],
    queryFn: () => api.video(videoId),
    enabled: !!videoId,
  });

  // --- Resolve facet count sources (per-video when scoped, else channel) -----

  const bySentiment = useMemo<Record<string, number>>(() => {
    if (videoId) return videoStats.data?.bySentiment ?? {};
    return overview.data?.comments.by_sentiment ?? {};
  }, [videoId, videoStats.data, overview.data]);

  const byGeneralTag = useMemo<Record<string, number>>(() => {
    if (videoId) return videoStats.data?.byGeneralTag ?? {};
    return overview.data?.comments.by_general_tag ?? {};
  }, [videoId, videoStats.data, overview.data]);

  const byTopicTag = useMemo<Record<string, number>>(() => {
    if (videoId) return videoStats.data?.byTopicTag ?? {};
    return overview.data?.comments.by_topic_tag ?? {};
  }, [videoId, videoStats.data, overview.data]);

  const topicLabels = useMemo<Record<string, string>>(() => {
    if (videoId) return videoStats.data?.topicLabels ?? {};
    return overview.data?.comments.topic_labels ?? {};
  }, [videoId, videoStats.data, overview.data]);

  // --- Render ----------------------------------------------------------------

  const loading = overview.isLoading || (!!videoId && videoStats.isLoading);
  const anyFilters = !!(
    sentiment ||
    gtag ||
    topic ||
    safety ||
    videoId ||
    highEngagement
  );
  const hasTopics = Object.keys(byTopicTag).length > 0;

  return (
    <div>
      <Header />

      <FilterChips
        sentiment={sentiment}
        gtag={gtag}
        topic={topic}
        topicLabel={topic ? (topicLabels[topic] ?? topic) : ""}
        safety={safety}
        videoId={videoId}
        videoTitle={video.data?.title ?? videoId}
        highEngagement={highEngagement}
        onToggleHighEngagement={() => toggleParam("he", highEngagement ? "" : "1")}
        onToggleSafety={(s) => toggleParam("safety", s)}
        onClear={clearParam}
        onClearAll={() => setSearchParams({})}
      />

      {loading ? (
        <DashboardSkeleton />
      ) : (
        <>
          <ChartsGrid>
            <ChartPanel title="Sentiment">
              <FacetBars
                counts={bySentiment}
                labelMap={SENTIMENT_LABELS}
                variant="sentiment"
                selected={sentiment || null}
                onSelect={(s) => toggleParam("sentiment", s)}
                emptyMessage="No sentiment tagged in this scope yet."
              />
            </ChartPanel>

            <ChartPanel title="What the audience is doing">
              <FacetBars
                counts={byGeneralTag}
                labelMap={GENERAL_TAG_LABELS}
                selected={gtag || null}
                onSelect={(g) => toggleParam("gtag", g)}
                emptyMessage="No general tags in this scope yet."
              />
            </ChartPanel>

            {hasTopics && (
              <ChartPanel title="Topics">
                <FacetBars
                  counts={byTopicTag}
                  labelMap={topicLabels}
                  selected={topic || null}
                  onSelect={(t) => toggleParam("topic", t)}
                  maxRows={TOP_TOPICS_LIMIT}
                  emptyMessage="No topics tagged in this scope yet."
                />
              </ChartPanel>
            )}
          </ChartsGrid>

          <CommentsListSection
            sentiment={sentiment}
            gtag={gtag}
            topic={topic}
            safety={safety}
            videoId={videoId}
            highEngagement={highEngagement}
            anyFilters={anyFilters}
          />
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                     */
/* -------------------------------------------------------------------------- */

function Header() {
  return (
    <div className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Comments
      </div>
      <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">
        Channel-wide dashboard
      </h1>
      <p className="mt-1.5 max-w-2xl text-sm text-ink-muted">
        Every comment the AI has read, sorted by tone, what the person is doing,
        and topic. Click any of them to filter the list below. You can combine
        filters (they stack), and pick a single video to focus on.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <HowItWorksCallout tone="light" />
      </div>
      <div className="mt-2.5 max-w-2xl rounded-lg border border-rule bg-black/2 px-3 py-2">
        <PrototypeNote tone="light" variant="inline" />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Filter chips                                                               */
/* -------------------------------------------------------------------------- */

function FilterChips({
  sentiment,
  gtag,
  topic,
  topicLabel,
  safety,
  videoId,
  videoTitle,
  highEngagement,
  onToggleHighEngagement,
  onToggleSafety,
  onClear,
  onClearAll,
}: {
  sentiment: string;
  gtag: string;
  topic: string;
  topicLabel: string;
  safety: string;
  videoId: string;
  videoTitle: string;
  highEngagement: boolean;
  onToggleHighEngagement: () => void;
  onToggleSafety: (s: SafetyTag) => void;
  onClear: (key: string) => void;
  onClearAll: () => void;
}) {
  const any = !!(
    sentiment ||
    gtag ||
    topic ||
    safety ||
    videoId ||
    highEngagement
  );

  return (
    <div className="sticky top-0 z-10 mb-6 -mx-4 flex flex-wrap items-center gap-2 border-b border-rule bg-paper/95 px-4 py-3 backdrop-blur sm:-mx-8 sm:px-8">
      <span className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Filters
      </span>
      {!any && (
        <span className="text-xs text-ink-muted">
          All comments · all facets
        </span>
      )}
      {videoId && (
        <Chip
          label={truncate(videoTitle || videoId, 36)}
          onRemove={() => onClear("videoId")}
        />
      )}
      {sentiment && (
        <Chip
          label={SENTIMENT_LABELS[sentiment] ?? sentiment}
          onRemove={() => onClear("sentiment")}
        />
      )}
      {gtag && (
        <Chip
          label={GENERAL_TAG_LABELS[gtag] ?? gtag}
          onRemove={() => onClear("gtag")}
        />
      )}
      {topic && <Chip label={topicLabel} onRemove={() => onClear("topic")} />}
      {safety && (
        <Chip
          label={SAFETY_TAG_LABELS[safety] ?? safety}
          onRemove={() => onClear("safety")}
        />
      )}
      {highEngagement && (
        <Chip label="High engagement" onRemove={onToggleHighEngagement} />
      )}

      {/* Add-filter affordances for the facets without a chart. */}
      {!safety &&
        SAFETY_TAGS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onToggleSafety(s)}
            className="rounded-full border border-rule bg-surface px-2 py-0.5 text-xs text-ink-muted hover:text-ink"
          >
            + {SAFETY_TAG_LABELS[s]}
          </button>
        ))}
      {!highEngagement && (
        <button
          type="button"
          onClick={onToggleHighEngagement}
          aria-pressed={false}
          className="rounded-full border border-rule bg-surface px-2 py-0.5 text-xs text-ink-muted hover:text-ink"
        >
          + High engagement
        </button>
      )}
      {any && (
        <button
          type="button"
          onClick={onClearAll}
          className="ml-auto text-[11px] text-ink-muted hover:text-ink"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-rule bg-surface px-2 py-0.5 text-xs text-ink">
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        className="ml-0.5 -mr-0.5 rounded-full p-0.5 text-ink-muted hover:bg-black/4 hover:text-ink"
      >
        <X size={11} />
      </button>
    </span>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

/* -------------------------------------------------------------------------- */
/* Charts layout                                                              */
/* -------------------------------------------------------------------------- */

function ChartsGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">{children}</div>
  );
}

function ChartPanel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  // Cap height only at xl+ where panels sit side-by-side and equal visual
  // weight matters. Below xl they stack — let them grow naturally.
  // Dark-terminal register (portal-wide design unification — cf. salience Panel).
  return (
    <section className="flex flex-col rounded-xl border border-white/10 bg-terminal xl:max-h-105">
      <h2 className="sticky top-0 z-1 rounded-t-xl border-b border-white/10 bg-terminal px-5 py-3 text-xs font-semibold uppercase tracking-wider text-terminal-ink-muted">
        {title}
      </h2>
      <div className="dark-scrollbar flex-1 px-5 py-4 xl:overflow-y-auto">{children}</div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Comments list                                                              */
/* -------------------------------------------------------------------------- */

function CommentsListSection({
  sentiment,
  gtag,
  topic,
  safety,
  videoId,
  highEngagement,
  anyFilters,
}: {
  sentiment: Sentiment | "";
  gtag: GeneralTag | "";
  topic: string;
  safety: SafetyTag | "";
  videoId: string;
  highEngagement: boolean;
  anyFilters: boolean;
}) {
  const [pages, setPages] = useState<
    Array<{ items: Comment[]; cont: string | null }>
  >([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const filterKey = `${sentiment}|${gtag}|${topic}|${safety}|${videoId}|${
    highEngagement ? "he" : ""
  }`;

  // Reset accumulated pages when filters change.
  useEffect(() => {
    setPages([]);
  }, [filterKey]);

  const params = {
    sentiment: sentiment || undefined,
    generalTag: gtag || undefined,
    topicTag: topic || undefined,
    safetyTag: safety || undefined,
    videoId: videoId || undefined,
    highEngagement: highEngagement || undefined,
  };

  // collectComments transparently follows the server's bounded-scan
  // continuations, so a screenful of matches comes back even when a facet is
  // sparse — no user-visible "keep scanning".
  const initial = useQuery({
    queryKey: [
      "dashboardComments",
      sentiment,
      gtag,
      topic,
      safety,
      videoId,
      highEngagement,
    ],
    queryFn: () => collectComments(params, PAGE_SIZE),
  });

  const items =
    pages.length > 0
      ? pages.flatMap((p) => p.items)
      : (initial.data?.items ?? []);
  const continuation =
    pages.length > 0
      ? pages[pages.length - 1].cont
      : (initial.data?.continuation ?? null);

  const loadMore = async () => {
    if (!continuation || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await collectComments(params, PAGE_SIZE, continuation);
      setPages((cur) => [
        ...(cur.length === 0 && initial.data
          ? [{ items: initial.data.items, cont: initial.data.continuation }]
          : cur),
        { items: next.items, cont: next.continuation },
      ]);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Comments
      </h2>

      {initial.isLoading && (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-lg bg-terminal-panel/50"
            />
          ))}
        </div>
      )}

      {initial.error && (
        <div className="text-sm text-ink-muted">
          Couldn't load comments.{" "}
          <button
            type="button"
            onClick={() => initial.refetch()}
            className="text-brand-indigo hover:underline"
          >
            Retry
          </button>
          .
        </div>
      )}

      {!initial.isLoading && items.length === 0 && (
        <div className="text-sm text-ink-muted">
          {anyFilters
            ? "No comments match these filters."
            : "No comments yet. They'll appear here once collection has run."}
        </div>
      )}

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((c) => (
            <CommentRow key={c.commentThreadId} comment={c} />
          ))}
        </ul>
      )}

      {continuation && (
        <div className="mt-4">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded border border-white/10 bg-terminal-panel px-3 py-1.5 text-xs font-semibold text-terminal-ink-muted transition-colors hover:text-terminal-ink disabled:opacity-50"
          >
            {loadingMore ? "Loading…" : `Load ${PAGE_SIZE} more`}
          </button>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Loading skeleton                                                           */
/* -------------------------------------------------------------------------- */

function DashboardSkeleton() {
  return (
    <div>
      <div className="mb-8 grid grid-cols-1 gap-6 xl:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="h-56 animate-pulse rounded-xl bg-terminal-panel/50"
          />
        ))}
      </div>
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-lg bg-terminal-panel/50"
          />
        ))}
      </div>
    </div>
  );
}
